/**
 * 经脉系统引擎
 */

import type { PlayerState } from './Player';
import { findRealmLevel } from '../data/realms';
import { REALMS } from '../data/realms';
import { MERIDIANS, getTotalPoints, getTotalOpened, type Meridian, type Acupoint } from '../data/meridians';
import { recalcStats } from './Player';

export interface OpenPointResult {
  success: boolean;
  message: string;
  costStones: number;
  costExp: number;
  pointName?: string;
}

/** 获取境界索引 */
function getRealmIndex(player: PlayerState): number {
  const realmLevel = findRealmLevel(player.realmKey);
  return realmLevel ? REALMS.findIndex((r) => r.levels.some((l) => l.key === player.realmKey)) : 0;
}

/** 灵根冲穴成功率加成 */
function rootBonus(player: PlayerState): number {
  const grade = player.spiritualRoot?.grade;
  switch (grade) {
    case '天': return 0.15;
    case '地': return 0.10;
    case '玄': return 0.05;
    default: return 0;
  }
}

/** 计算冲穴消耗灵石 */
export function calcPointCost(player: PlayerState, meridianId: string, pointIndex: number): number {
  const m = MERIDIANS.find((m) => m.id === meridianId);
  if (!m) return 0;
  const base = m.baseStoneCost * (pointIndex + 1);
  const realmMult = 1 + getRealmIndex(player) * 0.3;
  // 灵根折扣
  const discount = rootBonus(player) > 0.1 ? 0.8 : rootBonus(player) > 0.05 ? 0.9 : 1.0;
  return Math.floor(base * realmMult * discount);
}

/** 计算冲穴消耗修炼进度（百分比） */
export function calcPointExpCost(player: PlayerState, meridianId: string, pointIndex: number): number {
  const m = MERIDIANS.find((m) => m.id === meridianId);
  if (!m) return 0;
  const base = 5 + pointIndex * 3;
  return Math.min(50, base * m.difficulty);
}

/** 计算冲穴成功率 */
export function calcPointSuccessRate(player: PlayerState, meridianId: string, pointIndex: number, hasPill: boolean): number {
  const realmIdx = getRealmIndex(player);
  const base = 0.50 + realmIdx * 0.05 + rootBonus(player) - pointIndex * 0.03;
  const pillBonus = hasPill ? 0.20 : 0;
  // 功法加成
  const techId = player.techniqueId;
  let techBonus = 0;
  if (techId === 'gengjin_jue' && meridianId === 'chong') techBonus = 0.10;
  if (techId === 'qingmu_changsheng' && meridianId === 'yinqiao') techBonus = 0.10;
  if (techId === 'lihuo_fentian' && meridianId === 'yangqiao') techBonus = 0.15;
  if (techId === 'hunyuan_wuqi') techBonus = 0.05;
  return Math.min(0.95, Math.max(0.10, base + pillBonus + techBonus));
}

/** 获取玩家经脉数据 */
export function getPlayerMeridians(player: PlayerState): Record<string, number> {
  if (!player.meridians) (player as any).meridians = {};
  return player.meridians;
}

/** 执行冲穴 */
export function openAcupoint(player: PlayerState, meridianId: string): OpenPointResult {
  const m = MERIDIANS.find((m) => m.id === meridianId);
  if (!m) return { success: false, message: '经脉不存在', costStones: 0, costExp: 0 };

  const meridians = getPlayerMeridians(player);
  const current = meridians[meridianId] ?? 0;
  if (current >= m.points.length) {
    return { success: false, message: `「${m.name}」已全部贯通！`, costStones: 0, costExp: 0 };
  }

  const pointIdx = current;
  const point = m.points[pointIdx];
  const costStones = calcPointCost(player, meridianId, pointIdx);
  const costExpPct = calcPointExpCost(player, meridianId, pointIdx);

  // 检查灵石
  if (player.spiritStones < costStones) {
    return { success: false, message: `灵石不足！需要 ${costStones}，当前 ${player.spiritStones}`, costStones, costExp: 0 };
  }

  // 检查修炼进度
  const realmLevel = findRealmLevel(player.realmKey);
  if (!realmLevel) return { success: false, message: '境界异常', costStones: 0, costExp: 0 };
  const expNeeded = Math.floor(realmLevel.expRequired * costExpPct / 100);
  if (player.cultivationExp < expNeeded) {
    return { success: false, message: `修炼进度不足！需要 ${expNeeded} 经验（${costExpPct}%），当前 ${player.cultivationExp}`, costStones, costExp: expNeeded };
  }

  // 检查是否有通脉丹/护脉丹
  const pillIdx = player.inventory.findIndex((i) => i.id === 'meridian_pill');
  const hasPill = pillIdx !== -1;
  const shieldIdx = player.inventory.findIndex((i) => i.id === 'meridian_shield');
  const hasShield = shieldIdx !== -1;

  // 消耗灵石和经验
  player.spiritStones -= costStones;
  player.cultivationExp = Math.max(0, player.cultivationExp - expNeeded);

  // 消耗丹药
  if (hasPill) player.inventory.splice(pillIdx, 1);

  // 掷骰
  const rate = calcPointSuccessRate(player, meridianId, pointIdx, hasPill);
  const roll = Math.random();

  if (roll < rate) {
    // 成功
    meridians[meridianId] = current + 1;
    if (hasShield) player.inventory.splice(player.inventory.findIndex((i) => i.id === 'meridian_shield'), 1);
    recalcStats(player);
    return {
      success: true,
      message: `🔓 冲穴成功！${m.icon} ${m.name}·${point.name} 已贯通！${getPointBonusStr(point)}`,
      costStones,
      costExp: expNeeded,
      pointName: point.name,
    };
  } else {
    // 失败惩罚
    const failCount = (player as any)._meridianFailCount?.[meridianId]?.[pointIdx] ?? 0;
    if (!(player as any)._meridianFailCount) (player as any)._meridianFailCount = {};
    if (!(player as any)._meridianFailCount[meridianId]) (player as any)._meridianFailCount[meridianId] = {};
    (player as any)._meridianFailCount[meridianId][pointIdx] = failCount + 1;

    let msg = `💥 冲穴失败！${m.icon} ${m.name}·${point.name} 未能贯通。`;

    if (hasShield) {
      player.inventory.splice(player.inventory.findIndex((i) => i.id === 'meridian_shield'), 1);
      msg += ' 护脉丹保护了你的经脉，免受反噬。';
    } else {
      // 反噬伤害：基础20%HP × 难度系数
      const baseDmg = 0.20 + failCount * 0.10;
      const dmg = Math.floor(player.maxHp * baseDmg * m.difficulty);
      player.hp = Math.max(1, player.hp - dmg);
      msg += ` 经脉剧烈反噬，受到 ${dmg} 点伤害！`;

      // 额外修炼倒退
      const extraExpLoss = Math.floor(expNeeded * 0.5);
      player.cultivationExp = Math.max(0, player.cultivationExp - extraExpLoss);
      if (extraExpLoss > 0) msg += ` 修为倒退，额外损失 ${extraExpLoss} 点修炼经验。`;

      // 连续失败3次以上：15%概率穴位受损，倒退一个已通穴位
      if (failCount >= 2 && current > 0 && Math.random() < 0.15) {
        meridians[meridianId] = current - 1;
        msg += ` ⚠️ 经脉受损！「${m.name}」倒退了一个穴位！`;
      }
    }

    return { success: false, message: msg, costStones, costExp: expNeeded };
  }
}

function getPointBonusStr(point: Acupoint): string {
  const parts: string[] = [];
  if (point.hpBonus) parts.push(`HP+${point.hpBonus}`);
  if (point.mpBonus) parts.push(`MP+${point.mpBonus}`);
  if (point.attackBonus) parts.push(`攻+${point.attackBonus}`);
  if (point.defenseBonus) parts.push(`防+${point.defenseBonus}`);
  return parts.length > 0 ? `（${parts.join(' ')}）` : '';
}

/** 检查并激活经脉贯通效果 */
export function checkMasteryBuffs(player: PlayerState): string[] {
  const meridians = getPlayerMeridians(player);
  const msgs: string[] = [];
  for (const m of MERIDIANS) {
    const opened = meridians[m.id] ?? 0;
    if (opened >= m.points.length && !meridians[`${m.id}_mastered`]) {
      meridians[`${m.id}_mastered`] = 1;
      msgs.push(`🌟 经脉贯通！「${m.name}」全部穴位已冲开！${m.masteryDesc}`);
    }
  }
  return msgs;
}

/** 获取已贯通经脉数 */
export function getMasteredCount(player: PlayerState): number {
  const meridians = getPlayerMeridians(player);
  return MERIDIANS.filter((m) => (meridians[m.id] ?? 0) >= m.points.length).length;
}
